/*
  List all links available to one or more nav ia maps. LinkPermissionManager will
  build each link by name as a plain object, containing the entitlements that need
  to be checked in IaPermissionMapper.
*/
import resolveSubdomains from './resolve_subdomains';

const pubScope = permission => id => `publications.${id}.${permission}`;
const baseScope = permission => () => permission;

// [NAME, PATH, PERMISSION_KEY_FUNC, USE_SUBDOMAIN?]
export const linkData = [
  ['Analytics', '/analytics/:publication_id', pubScope('analytics.list'), false],
  [
    'Audiences',
    '/publications/:publication_id/content_strategy/audiences',
    pubScope('content_strategy.list'),
    false
  ],
  [
    'Brief templates',
    '/publications/:publication_id/brief_templates',
    pubScope('brief_templates.list'),
    false
  ],
  ['Calendar', '/stories/calendar', pubScope('stories.list'), false],
  ['Campaign', '/organizations/:account_id/campaigns/new', pubScope('campaigns.new'), false],
  ['Campaigns', '/organizations/:account_id/campaigns', pubScope('campaigns.list'), false],
  [
    'Command center',
    '/publications/:publication_id/command_center',
    baseScope('user.command_center'),
    false
  ],
  ['Completed', '/stories/library?statuses%5B%5D=completed', pubScope('stories.list'), false],
  ['Content', '/stories/library', pubScope('stories.list'), false],
  [
    'Content portals',
    '/publications/:publication_id/portals',
    pubScope('content_portals.list'),
    false
  ],
  [
    'Content plan',
    '/publications/:publication_id/content_strategy/plans',
    pubScope('content_strategy.list'),
    false
  ],
  [
    'Content request forms',
    '/publications/:publication_id/content_request_forms',
    pubScope('content_request_forms.list'),
    false
  ],
  ['Content requests', '/content_requests', pubScope('content_requests.list'), false],
  [
    'Content strategy',
    '/publications/:publication_id/content_strategy/',
    pubScope('content_strategy.list'),
    false
  ],
  [
    'Custom fields',
    '/publications/:publication_id/field_templates',
    pubScope('custom_fields.list'),
    false
  ],
  ['Digital assets', '/media?account_id=:account_id', pubScope('media.list'), false],
  [
    'Distribution',
    '/publications/:publication_id/content_strategy/distribution',
    pubScope('content_strategy.list'),
    false
  ],
  ['Draft', '/drafts/new', baseScope('user.drafts'), false],
  ['Drafts', '/drafts', baseScope('user.drafts'), false],
  ['Email', '/account/email_settings', null, false],
  ['Event', '/events/new', pubScope('events.new'), false],
  ['Financials', '/publications/:publication_id/transactions', pubScope('financials.list'), false],
  [
    'Publication activity',
    '/publications/:publication_id/invoices',
    pubScope('financials.list'),
    false
  ],
  [
    'Financial activity',
    '/publications/:publication_id/invoices',
    pubScope('financials.list'),
    false
  ],
  [
    'Goals',
    '/publications/:publication_id/content_strategy/mission_and_goals',
    pubScope('content_strategy.list'),
    false
  ],
  ['Ideation', '/pitches', pubScope('pitches.list'), false],
  ['In progress', '/stories/library?statuses%5B%5D=in_progress', pubScope('stories.list'), false],
  [
    'Integrations',
    '/publications/:publication_id/integrations',
    pubScope('integrations.list'),
    false
  ],
  ['Media', '/media', pubScope('media.list'), false],
  ['Messages', '/messages', baseScope('user.messages'), false],
  ['People', '/publications/:publication_id/users/teams', pubScope('users.list'), false],
  ['Password', '/account/password', null, false],
  [
    'Pillars',
    '/publications/:publication_id/content_strategy/pillars',
    pubScope('content_strategy.list'),
    false
  ],
  [
    'Pitch guidelines',
    '/publications/:publication_id/pitch_guidelines',
    pubScope('pitch_guidelines.list'),
    false
  ],
  ['Pitch request', '/pitch_requests/new', pubScope('pitch_requests.new'), false],
  ['Pitch', '/pitches/new', pubScope('pitches.new'), false],
  ['Pitches', '/pitches', pubScope('pitches.list'), false],
  ['Settings', '/publications', pubScope('publication_settings.list'), false],
  ['Publication settings', '/publications', pubScope('publication_settings.list'), false],
  ['Profile', '/account/profile', null, false],
  ['Recurring story', '/recurring_stories/new', pubScope('recurring_stories.new'), false],
  [
    'SEO keywords',
    '/publications/:publication_id/content_strategy/seo_keywords',
    pubScope('content_strategy.list'),
    false
  ],
  [
    'Summary',
    '/publications/:publication_id/content_strategy/report',
    pubScope('content_strategy.list'),
    false
  ],
  ['Sign out', '/signout', null, false],
  ['All stories', '/stories/library', pubScope('stories.list'), false],
  ['Stories', '/stories/library', pubScope('stories.list'), false],
  ['Story', '/stories/new', pubScope('stories.new'), false],
  ['Switch to Nav V2', null, baseScope('user.beta_v2_nav'), false],
  ['Switch to Nav V1', null, null, false],
  ['Tags', '/publications/:publication_id/tag_groups', pubScope('tag_groups.list'), false],
  [
    'Talent search',
    '/talent_requests/new?publication_id=:publication_id',
    pubScope('talent_requests.new'),
    false
  ],
  [
    'Talent',
    '/talent_requests?publication_id=:publication_id',
    pubScope('talent_requests.list'),
    false
  ],
  ['Tasks', '/tasks', pubScope('tasks.list'), false],
  ['Tasks (beta)', '/tasks/beta', pubScope('tasks.list'), false],
  [
    'Transactions',
    '/publications/:publication_id/transactions',
    pubScope('financials.list'),
    false
  ],
  ['Unassigned', '/stories/library?statuses%5B%5D=unassigned', pubScope('stories.list'), false],
  ['Upload story', '/stories/upload', pubScope('stories.upload.new'), false],
  ['User management', '/publications/:publication_id/users/teams', pubScope('users.list'), false],
  ['User roles', '/publications/:publication_id/staffer_roles', pubScope('user_roles.list'), false],
  ['User', '/publications/create_user', pubScope('users.new'), false],
  [
    'Voice & style',
    '/publications/:publication_id/content_strategy/voice_and_styles',
    pubScope('content_strategy.list'),
    false
  ],
  [
    'Workflow templates',
    '/publications/:publication_id/approval_flows',
    pubScope('workflow_templates.list'),
    false
  ],
  ['Your account', '/account', null, false],
  ['Your activity', '/account/activity', null, false],
  [
    'Organization settings',
    '/organizations/:account_id',
    pubScope('organization_settings.list'),
    false
  ],
  ['Portfolio', '/', baseScope('user.portfolio'), true] // MUST BE LAST - / path matches on all locations
];

export class Link {
  static ACCOUNT_ID_MATCHER = /:account_id/;

  static PUBLICATION_ID_MATCHER = /:publication_id/;

  static pathMatcher = path => {
    let regexPath = path.replace(/\//g, '\\/');
    // eslint-disable-next-line no-useless-escape
    regexPath = regexPath.replace(/:publication_id|:account_id/, '\\d*');
    return new RegExp(regexPath);
  };

  constructor(name, path, permission, useSubdomain, accountId, publicationId, portfolioName) {
    this.name = name;
    this.path = path;
    this.permission = permission;
    this.useSubdomain = useSubdomain;
    this.accountId = accountId;
    this.publicationId = publicationId;
    this.portfolioName = portfolioName;
  }

  get url() {
    let url = this.path;
    if (url) {
      url = url.replace(Link.ACCOUNT_ID_MATCHER, this.accountId);
      url = url.replace(Link.PUBLICATION_ID_MATCHER, this.publicationId);
      url = resolveSubdomains(this.portfolioName, url, this.useSubdomain);
    }
    return url;
  }

  serialize(publicationIds) {
    const { name, permission } = this;

    const linkObj = {
      name,
      path: this.url,
      action: this.action,
      permission
    };

    if (permission) {
      linkObj.entitlements = publicationIds.map(id => permission(id));
    }

    return linkObj;
  }
}

class LinkPermissionManager {
  static currentPageLink() {
    const pathPermissions = linkData.map(link => ({ path: link[1], permission: link[2] }));
    return pathPermissions.find(pp => Link.pathMatcher(pp.path).test(window.location.pathname));
  }

  constructor(portfolioName, currentAccountId, currentPublicationId, allPublicationIds) {
    this.links = linkData.map(
      link => new Link(...link, currentAccountId, currentPublicationId, portfolioName)
    );
    this.portfolioName = portfolioName;
    this.currentAccountId = currentAccountId;
    this.currentPublicationId = currentPublicationId;
    this.allPublicationIds = allPublicationIds;
  }

  findByName(name) {
    return this.links.find(link => link.name === name);
  }

  findByPath(path) {
    return this.links.find(link => link.path === path);
  }

  build(name, callbackAction) {
    const link = this.findByName(name);
    link.action = callbackAction;
    // eslint-disable-next-line no-console
    if (!link) return console.warn(name);
    return link.serialize(this.allPublicationIds);
  }
}

export default LinkPermissionManager;



// WEBPACK FOOTER //
// ./app/javascript/app/components/nav/link_permission_manager.js